import React from 'react'
import { RouteObject } from 'react-router-dom'
import { AppLayout } from '../components/layout'
import RequirePermission from '../components/auth/RequirePermission'
import NotFound from '../pages/common/NotFound'

// Lazy load pages for code splitting
const Dashboard = React.lazy(() => import('../pages/dashboard/Dashboard'))
const CustomerList = React.lazy(() => import('../pages/customers/CustomerList'))
const CustomerDetails = React.lazy(() => import('../pages/customers/CustomerDetails'))
const CustomerCreate = React.lazy(() => import('../pages/customers/CustomerOnboarding/CustomerOnboardingWizard'))
const CustomerCreateComplete = React.lazy(() => import('../pages/customers/CustomerCreateComplete'))
const CustomerEdit = React.lazy(() => import('../pages/customers/CustomerEditWizard/CustomerEditWizard'))
const CustomerChangeStatus = React.lazy(() => import('../pages/customers/CustomerStatusWorkflow'))
const CustomerChangeFreezeStatus = React.lazy(() => import('../pages/customers/CustomerFreezeWorkflow'))
const CustomerLinkExternalIdentity = React.lazy(() => import('../pages/customers/CustomerLinkExternalIdentityWorkflow'))
const CustomerChangeEmail = React.lazy(() => import('../pages/customers/CustomerChangeEmailWorkflow'))
const CustomerChangeTradingViewUsername = React.lazy(() => import('../pages/customers/CustomerChangeTradingViewUsernameWorkflow'))
const CustomerResetPassword = React.lazy(() => import('../pages/customers/CustomerResetPasswordWorkflow'))
const CustomerAddTradingStrategySubscription = React.lazy(() => import('../pages/customers/CustomerAddTradingStrategySubscription'))
const CustomerManageTradingStrategySubscription = React.lazy(() => import('../pages/customers/CustomerManageTradingStrategySubscription'))
const CustomerGrantOffer = React.lazy(() => import('../pages/customers/CustomerGrantOfferWorkflow'))
const CustomerAddMembership = React.lazy(() => import('../pages/customers/CustomerAddMembership'))
const CustomerEndMembership = React.lazy(() => import('../pages/customers/CustomerEndMembership'))
const CustomerResumeMembership = React.lazy(() => import('../pages/customers/CustomerResumeMembership'))
const CustomerReviewMembership = React.lazy(() => import('../pages/customers/CustomerReviewMembership'))
const CustomerAddAddOn = React.lazy(() => import('../pages/customers/CustomerAddAddOn'))
const CustomerEndAddOn = React.lazy(() => import('../pages/customers/CustomerEndAddOn'))
const CustomerResumeAddOn = React.lazy(() => import('../pages/customers/CustomerResumeAddOn'))
const CustomerInviteTradingViewScripts = React.lazy(() => import('../pages/customers/CustomerInviteTradingViewScripts'))
const CustomerRemoveTradingViewScripts = React.lazy(() => import('../pages/customers/CustomerRemoveTradingViewScripts'))
const SymbolList = React.lazy(() => import('../pages/symbols/SymbolList'))
const SymbolDetails = React.lazy(() => import('../pages/symbols/SymbolDetails'))
const SymbolCreate = React.lazy(() => import('../pages/symbols/SymbolCreate'))
const SymbolCreateComplete = React.lazy(() => import('../pages/symbols/SymbolCreateComplete'))
const SymbolEdit = React.lazy(() => import('../pages/symbols/SymbolEdit'))
const TradingStrategyList = React.lazy(() => import('../pages/trading-strategies/TradingStrategyList'))
const TradingStrategyDetails = React.lazy(() => import('../pages/trading-strategies/TradingStrategyDetails'))
const TradingStrategyCreate = React.lazy(() => import('../pages/trading-strategies/TradingStrategyCreate'))
const TradingStrategyEdit = React.lazy(() => import('../pages/trading-strategies/TradingStrategyEdit'))
const TradingStrategyChangeStatus = React.lazy(() => import('../pages/trading-strategies/TradingStrategyStatusWorkflow'))
const TradingStrategyEnableRealTimeUpdates = React.lazy(() => import('../pages/trading-strategies/TradingStrategyEnableRealTimeUpdatesWorkflow'))
const TradingStrategyTurnOffRealTimeUpdates = React.lazy(() => import('../pages/trading-strategies/TradingStrategyTurnOffRealTimeUpdates'))
const MembershipRequests = React.lazy(() => import('../pages/customer-memberships/MembershipRequests'))
const AccessToGrant = React.lazy(() => import('../pages/tradingview-script-invites/AccessToGrant'))
const AccessToRemove = React.lazy(() => import('../pages/tradingview-script-invites/AccessToRemove'))
const BrochureCatalogueList = React.lazy(() => import('../pages/brochure-catalogue/BrochureCatalogueList'))
const BrochureCatalogueCreate = React.lazy(() => import('../pages/brochure-catalogue/BrochureCatalogueCreateWizard'))
const BrochureCatalogueDetails = React.lazy(() => import('../pages/brochure-catalogue/BrochureCatalogueDetails'))
const BrochureCatalogueStatusWorkflow = React.lazy(() => import('../pages/brochure-catalogue/BrochureCatalogueStatusWorkflow'))
const PortfolioBuilderList = React.lazy(() => import('../pages/portfolio-builder/PortfolioBuilderList'))
const NewPortfolioBuilderRevisionWizard = React.lazy(() => import('../pages/portfolio-builder/NewPortfolioBuilderRevision'))
const PortfolioBuilderRevisionDetails = React.lazy(() => import('../pages/portfolio-builder/PortfolioBuilderRevisionDetails'))
const PortfolioBuilderRevisionStatusWorkflow = React.lazy(() => import('../pages/portfolio-builder/PortfolioBuilderRevisionStatusWorkflow'))
const PortfolioTrackerSetup = React.lazy(() => import('../pages/portfolio-tracker/PortfolioTrackerSetup'))
const PortfolioTrackerResults = React.lazy(() => import('../pages/portfolio-tracker/PortfolioTrackerResults'))
const PortfolioTrackerStrategy = React.lazy(() => import('../pages/portfolio-tracker/PortfolioTrackerStrategy'))
const PortfolioTrackerDifferences = React.lazy(() => import('../pages/portfolio-tracker/PortfolioTrackerDifferences'))
const OfferList = React.lazy(() => import('../pages/offers/OfferList'))
const OfferCreate = React.lazy(() => import('../pages/offers/OfferCreate'))
const OfferDetails = React.lazy(() => import('../pages/offers/OfferDetails'))
const OfferEdit = React.lazy(() => import('../pages/offers/OfferEdit'))
const OfferChangeStatus = React.lazy(() => import('../pages/offers/OfferStatusWorkflow'))
const OfferGrant = React.lazy(() => import('../pages/offers/OfferGrantWorkflow'))
const OfferRevokeGrant = React.lazy(() => import('../pages/offers/OfferRevokeGrantWorkflow'))
const AddOnList = React.lazy(() => import('../pages/add-ons/AddOnList'))
const AddOnDetails = React.lazy(() => import('../pages/add-ons/AddOnDetails'))
const AddOnEdit = React.lazy(() => import('../pages/add-ons/AddOnEdit'))
const AddOnChangeStatus = React.lazy(() => import('../pages/add-ons/AddOnStatusWorkflow'))
const MembershipPlatformList = React.lazy(() => import('../pages/memberships/MembershipPlatformList'))
const MembershipPlatformCreate = React.lazy(() => import('../pages/memberships/MembershipPlatformCreate'))
const MembershipPlatformDetails = React.lazy(() => import('../pages/memberships/MembershipPlatformDetails'))
const MembershipPlatformEdit = React.lazy(() => import('../pages/memberships/MembershipPlatformEdit'))
const MembershipTierCreate = React.lazy(() => import('../pages/memberships/tiers/MembershipTierCreate'))
const MembershipTierDetails = React.lazy(() => import('../pages/memberships/tiers/MembershipTierDetails'))
const MembershipTierEdit = React.lazy(() => import('../pages/memberships/tiers/MembershipTierEdit'))
const MembershipTierChangeStatus = React.lazy(() => import('../pages/memberships/tiers/MembershipTierStatusWorkflow'))
const IncomingSignalList = React.lazy(() => import('../pages/incoming-signals/IncomingSignalList'))
const IncomingSignalDetails = React.lazy(() => import('../pages/incoming-signals/IncomingSignalDetails'))
const IncomingSignalWebhookUrls = React.lazy(() => import('../pages/incoming-signals/IncomingSignalWebhookUrls'))
const IncomingSignalReplay = React.lazy(() => import('../pages/incoming-signals/IncomingSignalReplayWorkflow'))
const IncomingSignalSend = React.lazy(() => import('../pages/incoming-signals/IncomingSignalSendWorkflow'))
const StrategyDataAlertList = React.lazy(() => import('../pages/strategy-data-alerts/StrategyDataAlertList'))
const StrategyDataAlertDetails = React.lazy(() => import('../pages/strategy-data-alerts/StrategyDataAlertDetails'))
const StrategyDataAlertWebhookUrls = React.lazy(() => import('../pages/strategy-data-alerts/StrategyDataAlertWebhookUrls'))
const InfoAlertList = React.lazy(() => import('../pages/info-alerts/InfoAlertList'))
const InfoAlertDetails = React.lazy(() => import('../pages/info-alerts/InfoAlertDetails'))
const InfoAlertWebhookUrls = React.lazy(() => import('../pages/info-alerts/InfoAlertWebhookUrls'))
const UnknownAlertList = React.lazy(() => import('../pages/unknown-alerts/UnknownAlertList'))
const UnknownAlertDetails = React.lazy(() => import('../pages/unknown-alerts/UnknownAlertDetails'))
const CustomerFeedbackQueue = React.lazy(() => import('../pages/customer-feedback/CustomerFeedbackQueue'))
const TriageFeedbackWorkflow = React.lazy(() => import('../pages/customer-feedback/TriageFeedbackWorkflow'))
const StrategyRequestsPage = React.lazy(() => import('../pages/strategy-requests/StrategyRequestsPage'))
const CleanseStrategyRequest = React.lazy(() => import('../pages/strategy-requests/CleanseStrategyRequest'))
const OutstandingActionsDigestRuns = React.lazy(() => import('../pages/outstandingActions/OutstandingActionsDigestRuns'))
const TriggerOutstandingActionsDigestWizard = React.lazy(() => import('../pages/outstandingActions/TriggerDigest'))
const MarketCandleRuns = React.lazy(() => import('../pages/marketCandles/MarketCandleRuns'))
const MarketCandleRunDetails = React.lazy(() => import('../pages/marketCandles/MarketCandleRunDetails'))
const FetchMarketCandlesWizard = React.lazy(() => import('../pages/marketCandles/FetchMarketCandles'))
const DevTools = React.lazy(() => import('../pages/devtools/DevTools'))
const PermissionsAdmin = React.lazy(() => import('../pages/admin/PermissionsAdmin'))
const Processes = React.lazy(() => import('../pages/processes/Processes'))
const FillStrategyHistoryWizard = React.lazy(() => import('../pages/processes/FillStrategyHistory'))
const RecalculateFromStoredDataWizard = React.lazy(() => import('../pages/processes/RecalculateFromStoredData'))
const TurnOnRealTimeUpdatesWizard = React.lazy(() => import('../pages/processes/TurnOnRealTimeUpdates'))
const Unauthorized = React.lazy(() => import('../pages/common/Unauthorized'))

const routes: RouteObject[] = [
  {
    path: '/',
    element: <AppLayout />,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      // Customers
      {
        path: 'customers',
        element: <CustomerList />,
      },
      {
        path: 'customers/create',
        element: <RequirePermission permission="customers:onboard"><CustomerCreate /></RequirePermission>,
      },
      {
        path: 'customers/create/complete/:id',
        element: <RequirePermission permission="customers:onboard"><CustomerCreateComplete /></RequirePermission>,
      },
      {
        path: 'customers/:id',
        element: <CustomerDetails />,
      },
      {
        path: 'customers/:id/edit',
        element: <RequirePermission permission="customers:edit"><CustomerEdit /></RequirePermission>,
      },
      {
        path: 'customers/:id/change-status',
        element: <CustomerChangeStatus />,
      },
      {
        path: 'customers/:id/change-freeze-status',
        element: <CustomerChangeFreezeStatus />,
      },
      {
        path: 'customers/:id/link-external-identity',
        element: <RequirePermission permission="customers:provision-external-identity"><CustomerLinkExternalIdentity /></RequirePermission>,
      },
      {
        path: 'customers/:id/change-email',
        element: <RequirePermission permission="customers:change-email"><CustomerChangeEmail /></RequirePermission>,
      },
      {
        path: 'customers/:id/change-tradingview-username',
        element: <RequirePermission permission="customers:change-tradingview-username"><CustomerChangeTradingViewUsername /></RequirePermission>,
      },
      {
        path: 'customers/:id/reset-password',
        element: <RequirePermission permission="customers:reset-password"><CustomerResetPassword /></RequirePermission>,
      },
      {
        path: 'customers/:id/trading-strategy-subscriptions/add',
        element: <RequirePermission permission="trading-strategy-subscriptions:add"><CustomerAddTradingStrategySubscription /></RequirePermission>,
      },
      {
        path: 'customers/:id/trading-strategy-subscriptions/:strategyId/manage',
        element: <CustomerManageTradingStrategySubscription />,
      },
      {
        path: 'customers/:id/grant-offer',
        element: <RequirePermission permission="offers:grant"><CustomerGrantOffer /></RequirePermission>,
      },
      {
        path: 'customers/:id/memberships/add',
        element: <RequirePermission permission="customer-memberships:add"><CustomerAddMembership /></RequirePermission>,
      },
      {
        path: 'customers/:id/memberships/:membershipId/end',
        element: <RequirePermission permission="customer-memberships:end"><CustomerEndMembership /></RequirePermission>,
      },
      {
        path: 'customers/:id/memberships/:membershipId/resume',
        element: <RequirePermission permission="customer-memberships:resume"><CustomerResumeMembership /></RequirePermission>,
      },
      {
        path: 'customers/:id/memberships/:membershipId/review',
        element: <RequirePermission permission="customer-memberships:approve"><CustomerReviewMembership /></RequirePermission>,
      },
      {
        path: 'customers/:id/add-ons/add',
        element: <RequirePermission permission="customer-add-ons:add"><CustomerAddAddOn /></RequirePermission>,
      },
      {
        path: 'customers/:id/add-ons/:addOnId/end',
        element: <RequirePermission permission="customer-add-ons:end"><CustomerEndAddOn /></RequirePermission>,
      },
      {
        path: 'customers/:id/add-ons/:addOnId/resume',
        element: <RequirePermission permission="customer-add-ons:resume"><CustomerResumeAddOn /></RequirePermission>,
      },
      {
        path: 'customers/:id/tradingview-scripts/invite',
        element: <RequirePermission permission="tradingview-scripts:invite"><CustomerInviteTradingViewScripts /></RequirePermission>,
      },
      {
        path: 'customers/:id/tradingview-scripts/remove',
        element: <RequirePermission permission="tradingview-scripts:remove"><CustomerRemoveTradingViewScripts /></RequirePermission>,
      },
      // Symbols
      {
        path: 'symbols',
        element: <SymbolList />,
      },
      {
        path: 'symbols/create',
        element: <RequirePermission permission="symbols:create"><SymbolCreate /></RequirePermission>,
      },
      {
        path: 'symbols/create/complete/:id',
        element: <RequirePermission permission="symbols:create"><SymbolCreateComplete /></RequirePermission>,
      },
      {
        path: 'symbols/:id',
        element: <SymbolDetails />,
      },
      {
        path: 'symbols/:id/edit',
        element: <RequirePermission permission="symbols:edit"><SymbolEdit /></RequirePermission>,
      },
      // Trading Strategies
      {
        path: 'trading-strategies',
        element: <TradingStrategyList />,
      },
      {
        path: 'trading-strategies/create',
        element: <RequirePermission permission="trading-strategies:create"><TradingStrategyCreate /></RequirePermission>,
      },
      {
        path: 'trading-strategies/:id',
        element: <TradingStrategyDetails />,
      },
      {
        path: 'trading-strategies/:id/edit',
        element: <RequirePermission permission="trading-strategies:edit"><TradingStrategyEdit /></RequirePermission>,
      },
      {
        path: 'trading-strategies/:id/change-status',
        element: <TradingStrategyChangeStatus />,
      },
      {
        path: 'trading-strategies/:id/enable-real-time-updates',
        element: <RequirePermission permission="admin:run-processes"><TradingStrategyEnableRealTimeUpdates /></RequirePermission>,
      },
      {
        path: 'trading-strategies/:id/turn-off-real-time-updates',
        element: <RequirePermission permission="admin:run-processes"><TradingStrategyTurnOffRealTimeUpdates /></RequirePermission>,
      },
      // Customer feedback (cross-strategy queue)
      {
        path: 'customer-feedback',
        element: <CustomerFeedbackQueue />,
      },
      // Strategy requests (what customers have asked us to build)
      {
        path: 'strategy-requests',
        element: <StrategyRequestsPage />,
      },
      {
        path: 'strategy-requests/:id/cleanse',
        element: (
          <RequirePermission permission="strategy-requests:cleanse">
            <CleanseStrategyRequest />
          </RequirePermission>
        ),
      },
      {
        path: 'customer-feedback/:id/triage',
        element: <RequirePermission permission="customer-feedback:triage"><TriageFeedbackWorkflow /></RequirePermission>,
      },
      // Customer Memberships (cross-customer)
      {
        path: 'customer-memberships/requests',
        element: <RequirePermission permission="customer-memberships:approve"><MembershipRequests /></RequirePermission>,
      },
      // TradingView script invites (cross-customer)
      {
        path: 'tradingview-script-invites/access-to-grant',
        element: <RequirePermission permission="tradingview-scripts:invite"><AccessToGrant /></RequirePermission>,
      },
      {
        path: 'tradingview-script-invites/access-to-remove',
        element: <RequirePermission permission="tradingview-scripts:remove"><AccessToRemove /></RequirePermission>,
      },
      // Brochure Catalogue
      {
        path: 'brochure-catalogue',
        element: <BrochureCatalogueList />,
      },
      {
        path: 'brochure-catalogue/new',
        element: <BrochureCatalogueCreate />,
      },
      {
        path: 'brochure-catalogue/:id/change-status',
        element: <BrochureCatalogueStatusWorkflow />,
      },
      {
        path: 'brochure-catalogue/:id',
        element: <BrochureCatalogueDetails />,
      },
      // Portfolio builder
      {
        path: 'portfolio-builder',
        element: <PortfolioBuilderList />,
      },
      {
        path: 'portfolio-builder/new',
        element: (
          <RequirePermission permission="portfolio-builder-revisions:create">
            <NewPortfolioBuilderRevisionWizard />
          </RequirePermission>
        ),
      },
      {
        path: 'portfolio-builder/:id/change-status',
        element: (
          <RequirePermission permission="portfolio-builder-revisions:activate">
            <PortfolioBuilderRevisionStatusWorkflow />
          </RequirePermission>
        ),
      },
      {
        path: 'portfolio-builder/:id',
        element: <PortfolioBuilderRevisionDetails />,
      },
      // Portfolio tracker
      {
        path: 'portfolio-tracker',
        element: <PortfolioTrackerSetup />,
      },
      {
        path: 'portfolio-tracker/results',
        element: <PortfolioTrackerResults />,
      },
      {
        path: 'portfolio-tracker/results/differences',
        element: <PortfolioTrackerDifferences />,
      },
      {
        path: 'portfolio-tracker/results/strategies/:key',
        element: <PortfolioTrackerStrategy />,
      },
      // Offers & promotions
      {
        path: 'offers',
        element: <OfferList />,
      },
      {
        path: 'offers/create',
        element: <RequirePermission permission="offers:create"><OfferCreate /></RequirePermission>,
      },
      {
        path: 'offers/:id',
        element: <OfferDetails />,
      },
      {
        path: 'offers/:id/edit',
        element: <RequirePermission permission="offers:edit"><OfferEdit /></RequirePermission>,
      },
      {
        path: 'offers/:id/change-status',
        element: <OfferChangeStatus />,
      },
      {
        path: 'offers/:id/grant',
        element: <RequirePermission permission="offers:grant"><OfferGrant /></RequirePermission>,
      },
      {
        path: 'offers/:id/grants/:customerId/revoke',
        element: <RequirePermission permission="offers:grant"><OfferRevokeGrant /></RequirePermission>,
      },
      // Memberships
      {
        path: 'memberships',
        element: <MembershipPlatformList />,
      },
      {
        path: 'memberships/create',
        element: <RequirePermission permission="memberships:create"><MembershipPlatformCreate /></RequirePermission>,
      },
      {
        path: 'memberships/:id',
        element: <MembershipPlatformDetails />,
      },
      {
        path: 'memberships/:id/edit',
        element: <RequirePermission permission="memberships:edit"><MembershipPlatformEdit /></RequirePermission>,
      },
      {
        path: 'memberships/:id/tiers/create',
        element: <RequirePermission permission="membership-tiers:create"><MembershipTierCreate /></RequirePermission>,
      },
      {
        path: 'memberships/:id/tiers/:tierId',
        element: <MembershipTierDetails />,
      },
      {
        path: 'memberships/:id/tiers/:tierId/edit',
        element: <RequirePermission permission="membership-tiers:edit"><MembershipTierEdit /></RequirePermission>,
      },
      {
        path: 'memberships/:id/tiers/:tierId/change-status',
        element: <MembershipTierChangeStatus />,
      },
      // Add-ons
      {
        path: 'add-ons',
        element: <AddOnList />,
      },
      {
        path: 'add-ons/:id',
        element: <AddOnDetails />,
      },
      {
        path: 'add-ons/:id/edit',
        element: <RequirePermission permission="add-ons:edit"><AddOnEdit /></RequirePermission>,
      },
      {
        path: 'add-ons/:id/change-status',
        element: <AddOnChangeStatus />,
      },
      // Incoming Signals
      {
        path: 'incoming-signals',
        element: <IncomingSignalList />,
      },
      {
        path: 'incoming-signals/webhook-urls',
        element: <RequirePermission permission="incoming-signals:view-webhook-urls"><IncomingSignalWebhookUrls /></RequirePermission>,
      },
      {
        path: 'incoming-signals/send',
        element: <RequirePermission permission="incoming-signals:send"><IncomingSignalSend /></RequirePermission>,
      },
      {
        path: 'incoming-signals/:id',
        element: <IncomingSignalDetails />,
      },
      {
        path: 'incoming-signals/:id/replay',
        element: <RequirePermission permission="incoming-signals:replay"><IncomingSignalReplay /></RequirePermission>,
      },
      // Strategy Data Alerts
      {
        path: 'strategy-data-alerts',
        element: <StrategyDataAlertList />,
      },
      {
        path: 'strategy-data-alerts/webhook-urls',
        element: <RequirePermission permission="strategy-data-alerts:view-webhook-urls"><StrategyDataAlertWebhookUrls /></RequirePermission>,
      },
      {
        path: 'strategy-data-alerts/:id',
        element: <StrategyDataAlertDetails />,
      },
      // Info Alerts
      {
        path: 'info-alerts',
        element: <InfoAlertList />,
      },
      {
        path: 'info-alerts/webhook-urls',
        element: <RequirePermission permission="info-alerts:view-webhook-urls"><InfoAlertWebhookUrls /></RequirePermission>,
      },
      {
        path: 'info-alerts/:id',
        element: <InfoAlertDetails />,
      },
      // Unknown Alerts
      {
        path: 'unknown-alerts',
        element: <UnknownAlertList />,
      },
      {
        path: 'unknown-alerts/:id',
        element: <UnknownAlertDetails />,
      },
      // Outstanding actions digest
      {
        path: 'outstanding-actions/digest-runs',
        element: <OutstandingActionsDigestRuns />,
      },
      {
        path: 'outstanding-actions/digest-runs/trigger',
        element: (
          <RequirePermission permission="outstanding-actions:trigger-digest">
            <TriggerOutstandingActionsDigestWizard />
          </RequirePermission>
        ),
      },
      // Market candles
      {
        path: 'market-candles',
        element: <MarketCandleRuns />,
      },
      {
        path: 'market-candles/fetch',
        element: (
          <RequirePermission permission="market-candles:fetch">
            <FetchMarketCandlesWizard />
          </RequirePermission>
        ),
      },
      {
        path: 'market-candles/:id',
        element: <MarketCandleRunDetails />,
      },
      // Developer Tools
      {
        path: 'dev-tools',
        element: <RequirePermission permission="dev-tools:wake-up"><DevTools /></RequirePermission>,
      },
      // Admin
      {
        path: 'admin/permissions',
        element: <RequirePermission permission="admin:manage-permissions"><PermissionsAdmin /></RequirePermission>,
      },
      // Processes
      {
        path: 'processes',
        element: <RequirePermission permission="admin:run-processes"><Processes /></RequirePermission>,
      },
      {
        path: 'processes/fill-strategy-history',
        element: <RequirePermission permission="admin:run-processes"><FillStrategyHistoryWizard /></RequirePermission>,
      },
      {
        path: 'processes/recalculate-from-stored-data',
        element: <RequirePermission permission="admin:run-processes"><RecalculateFromStoredDataWizard /></RequirePermission>,
      },
      {
        path: 'processes/turn-on-real-time-updates',
        element: <RequirePermission permission="admin:run-processes"><TurnOnRealTimeUpdatesWizard /></RequirePermission>,
      },
      // Unauthorized
      {
        path: 'unauthorized',
        element: <Unauthorized />,
      },
      // 404 catch-all (must be last)
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]

export default routes
